import { db } from '../firebase';
import { getAuth, onAuthStateChanged, deleteUser as firebaseDeleteUser, createUserWithEmailAndPassword, sendEmailVerification, signOut } from 'firebase/auth';
import { collection, getDocs, getDoc, addDoc, updateDoc, doc, deleteDoc, query, orderBy, where, setDoc, serverTimestamp } from 'firebase/firestore';

import { getFunctions, httpsCallable } from "firebase/functions";

const userCollection = collection(db, "users");

// Vérifie que l'utilisateur est connecté et retourne son objet auth
export const getCurrentUser = async () => {
  const auth = getAuth();
  return new Promise((resolve, reject) => {
    const unsubscribe = onAuthStateChanged(
      auth,
      (user) => {
        unsubscribe();
        resolve(user);
      },
      reject
    );
  });
};

export const getEffectiveUserId = async () => {
  const authUser = await getCurrentUser();
  if (!authUser) throw new Error("Utilisateur non connecté");
  const userDoc = await getUserById(authUser.uid);
  return userDoc.mainUserId || authUser.uid;
};

export const getCurrentUserPermission = async (key) => {
  const authUser = await getCurrentUser();
  if (!authUser) return "none";
  const user = await getUserById(authUser.uid);
  if (!user.permissions || !user.permissions[key]) return "none";
  return user.permissions[key].permission || "none";
};

export const fetchUsers = async () => {
  const userId = await getEffectiveUserId();
  if (!userId) {
    throw new Error("Utilisateur non connecté");
  }

  await syncSubUsersWithPlan(); // Assure que les sous-users sont synchronisés avec le plan

  // Récupère le main user
  const mainUserQuery = query(
    userCollection, 
    where("id", "==", userId),
  );
  const mainUserSnapshot = await getDocs(mainUserQuery);
  const mainUser = mainUserSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

  // Récupère les sous-users du main user
  const usersQuery = query(
    userCollection, 
    where("mainUserId", "==", userId),
  );
  const usersSnapshot = await getDocs(usersQuery);
  const users = usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

  // Trie les sous-users par order croissant
  users.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  // Combine en mettant le main user en premier (et évite les doublons si jamais)
  const allUsers = [
    ...mainUser,
    ...users.filter(u => !mainUser.some(mu => mu.id === u.id))
  ];

  return allUsers || [];
};

export const getUserById = async (userId) => {
  const userRef = doc(db, "users", userId);
  const userDoc = await getDoc(userRef);
  if (!userDoc.exists()) {
    throw new Error("Utilisateur non trouvé");
  }
  return { id: userDoc.id, ...userDoc.data() };
}

export const addUser = async (user) => {
  const userId = (await getCurrentUser()).uid;
  if (!userId) {
    throw new Error("Utilisateur non connecté");
  }
  // Vous pouvez également ajouter le userId dans le document si besoin
  const users = await fetchUsers();
  const newOrder = users.length > 0 ? users[users.length - 1].order + 1 : 0;
  const newUser = { ...user, order: newOrder, userId: userId };

  const docRef = await addDoc(userCollection, newUser);
  return { id: docRef.id, ...newUser };
};

export const updateUser = async (user) => {
  const userRef = doc(db, "users", user.id);
  await updateDoc(userRef, { ...user });
};

// Fonction utilitaire pour générer un mot de passe aléatoire
function generateRandomPassword(length = 10) {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
  let pwd = '';
  for (let i = 0; i < length; i++) {
    pwd += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return pwd;
}

export const addNewUser = async (user) => {
  if (!user || !user.uid || !user.email) throw new Error("Informations utilisateur incomplètes");

  // Définition du plan
  const plan = {
    name: "trial",
    maxRessources: 10,
    maxUsers: 3,
  };

  // Permissions par défaut
  const defaultPermissions = {
    planning: { name: "Planning", order: 0, permission: "modification" },
    projects: { name: "Projets", order: 1, permission: "modification" },
    cash: { name: "Trésorerie", order: 2, permission: "modification" },
    ideas: { name: "Boîte à idées", order: 3, permission: "modification" },
    ressources: { name: "Gestion des ressources", order: 4, permission: "modification" },
    clients: { name: "Clients", order: 5, permission: "modification" },
    users: { name: "Utilisateurs", order: 6, permission: "consultation" },
    reminders: { name: "Rappels pour les actions", order: 7, permission: "modification" }
  };

  // 1. Création du user principal
  await setDoc(doc(db, "users", user.uid), {
    id: user.uid,
    email: user.email,
    createdAt: serverTimestamp(),
    plan,
    name: "Compte Principal",
    permissions: defaultPermissions,
    requiresEmailVerification: true
  });

  // 2. Création des sous-users
  const maxUsers = plan.maxUsers || 1;
  const sousUsersCollection = collection(db, "users");
  const baseEmail = user.email.split('@');
  const base = baseEmail[0];
  const domain = baseEmail[1];
  const tempPassword = "123456";

  const auth = getAuth();

  // Premier sous-user : mêmes permissions que le principal
  if (maxUsers > 1) {
    const email1 = `${base}+1@${domain}`;
    let sousUser1;
    let sousUser1Id = null;
    const password1 = generateRandomPassword();
    try {
      sousUser1 = await createUserWithEmailAndPassword(auth, email1, password1);
      sousUser1Id = sousUser1.user.uid;
    } catch (e) {
      // Si le compte existe déjà, on ne le crée pas côté Auth, mais on continue côté Firestore
      // Tu peux ici retrouver l'uid existant si besoin
    }
    if (sousUser1Id) {
      await setDoc(doc(db, "users", sousUser1Id), {
        id: sousUser1Id,
        email: email1,
        createdAt: serverTimestamp(),
        name: "Utilisateur supplémentaire 1",
        mainUserId: user.uid,
        permissions: defaultPermissions,
        requiresEmailVerification: false,
        order: 1,
        defaultPassword: password1 // <-- Ajoute le mot de passe ici
      });
    }
  }

  // Les autres sous-users : tout en consultation
  for (let i = 2; i <= maxUsers - 1; i++) {
    const consultPermissions = {};
    for (const key in defaultPermissions) {
      consultPermissions[key] = {
        ...defaultPermissions[key],
        permission: "consultation"
      };
    }
    const emailI = `${base}+${i}@${domain}`;
    let sousUserId = null;
    const passwordI = generateRandomPassword();
    try {
      const sousUser = await createUserWithEmailAndPassword(auth, emailI, passwordI);
      sousUserId = sousUser.user.uid;
    } catch (e) {
      // Si le compte existe déjà, on ne le crée pas côté Auth, mais on continue côté Firestore
      // Tu peux ici retrouver l'uid existant si besoin
    }
    if (sousUserId) {
      await setDoc(doc(db, "users", sousUserId), {
        id: sousUserId,
        email: emailI,
        createdAt: serverTimestamp(),
        name: `Utilisateur supplémentaire ${i}`,
        mainUserId: user.uid,
        permissions: consultPermissions,
        requiresEmailVerification: false,
        order: i,
        defaultPassword: passwordI // <-- Ajoute le mot de passe ici aussi
      });
    }
  }
};

export const deleteUserAndData = async () => {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error("Aucun utilisateur connecté");

  // 1. Obtiens l'instance Functions du SDK client
  const functions = getFunctions(); // <-- c'est ça qui doit être passé à httpsCallable

  // 2. Appelle la Cloud Function
  const deleteAll = httpsCallable(functions, "deleteMainUserAndSubs");
  console.log("Appel de la fonction deleteMainUserAndSubs pour l'utilisateur :", user.uid);
  await deleteAll({ mainUserId: user.uid });

  // 3. Déconnecte l'utilisateur
  await signOut(auth);
};

export const syncSubUsersWithPlan = async () => {
  const authUser = await getCurrentUser();
  if (!authUser) throw new Error("Utilisateur non connecté");

  // 1. Récupère le main user
  const mainUser = await getUserById(authUser.uid);
  const maxUsers = mainUser.plan?.maxUsers || 1;
  console.log(`[SYNC] mainUser.id=${mainUser.id}, maxUsers=${maxUsers}`);

  // 2. Récupère tous les sous-users
  const usersQuery = query(
    userCollection,
    where("mainUserId", "==", mainUser.id)
  );
  const usersSnapshot = await getDocs(usersQuery);
  let subUsers = usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

  // Trie par 'order'
  subUsers = subUsers.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  console.log(`[SYNC] Nombre de sous-users Firestore: ${subUsers.length}`, subUsers.map(u => u.id));

  // 3. Compare et ajuste
  const wantedSubUsers = maxUsers - 1;
  console.log(`[SYNC] wantedSubUsers (maxUsers - 1): ${wantedSubUsers}`);

  const functions = getFunctions();

  if (subUsers.length > wantedSubUsers) {
    console.log(`[SYNC] Trop de sous-users, suppression nécessaire (${subUsers.length} > ${wantedSubUsers})`);
    // Supprime ceux en trop (les derniers)
    const toDelete = subUsers.slice(wantedSubUsers);
    const deleteSubUser = httpsCallable(functions, "deleteSubUser");
    for (const user of toDelete) {
      console.log(`[SYNC] Suppression du sous-user Firestore/Auth :`, user.id, user.email);
      try {
        const res = await deleteSubUser({ uid: user.id });
        console.log(`[SYNC] Suppression réussie pour`, user.id, res);
      } catch (e) {
        console.error(`[SYNC] Erreur suppression sous-user`, user.id, e);
      }
    }
  } else if (subUsers.length < wantedSubUsers) {
    console.log(`[SYNC] Pas assez de sous-users, création nécessaire (${subUsers.length} < ${wantedSubUsers})`);
    // Ajoute ceux qui manquent via la Cloud Function
    const baseEmail = mainUser.email.split('@');
    const base = baseEmail[0];
    const domain = baseEmail[1];
    const defaultPermissions = mainUser.permissions;
    const createSubUser = httpsCallable(functions, "createSubUser");
    for (let i = subUsers.length + 1; i <= wantedSubUsers; i++) {
      const emailI = `${base}+${i}@${domain}`;
      const passwordI = generateRandomPassword();
      // Permissions consultation
      const consultPermissions = {};
      for (const key in defaultPermissions) {
        consultPermissions[key] = {
          ...defaultPermissions[key],
          permission: "consultation"
        };
      }
      try {
        const res = await createSubUser({
          email: emailI,
          password: passwordI,
          mainUserId: mainUser.id,
          name: `Utilisateur supplémentaire ${i}`,
          permissions: consultPermissions,
          order: i,
        });
        console.log(`[SYNC] Création sous-user via Cloud Function OK :`, emailI, res.data.uid);
      } catch (e) {
        console.warn(`[SYNC] Erreur création sous-user via Cloud Function :`, emailI, e);
      }
    }
  } else {
    console.log(`[SYNC] Nombre de sous-users correct (${subUsers.length} == ${wantedSubUsers}), aucune action.`);
  }
};